import { t as uiText } from '../../i18n';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Timestamp } from 'firebase/firestore';
import { storage } from '../../lib/firebase';
import type { ThreadAttachment } from './types';

/** 永久ファイル: 100MB */
export const MAX_PERMANENT_FILE_SIZE = 100 * 1024 * 1024;
/** 一時ファイル: 1GB（30日後に期限切れ） */
export const MAX_TEMPORARY_FILE_SIZE = 1024 * 1024 * 1024;

const sanitizeFilename = (name: string): string =>
  name.replace(/[\u0000-\u001f<>:"/\\|?*]+/g, '').slice(0, 200);

export interface UploadThreadTemporaryAttachmentParams {
  workspaceId: string;
  threadId: string;
  targetType: 'post' | 'comment';
  targetId: string;
  file: File;
  onProgress?: (ratio: number) => void;
}

export interface UploadedThreadTemporaryAttachment extends ThreadAttachment {}

/**
 * スレッド添付ファイルを Firebase Storage にアップロードし、メタデータを返す。
 * - temporary: 30日後に期限切れとなるパスに保存（容量カウントなし）
 */
export const uploadThreadTemporaryAttachment = async ({
  workspaceId,
  threadId,
  targetType,
  targetId,
  file,
  onProgress,
}: UploadThreadTemporaryAttachmentParams): Promise<UploadedThreadTemporaryAttachment> => {
  if (file.size > MAX_TEMPORARY_FILE_SIZE) {
    throw new Error(
      uiText("{{name}} is over 1GB and cannot be attached. Compress or split the file and try again.", { name: file.name })
    );
  }

  const attachmentId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const sanitized = sanitizeFilename(file.name);

  const storagePath = `${workspaceId}/thread-temp-files/${threadId}/${targetType}/${targetId}/${attachmentId}/${sanitized}`;

  const storageRef = ref(storage, storagePath);
  const task = uploadBytesResumable(storageRef, file, {
    contentType: file.type || undefined,
  });

  const snapshot = await new Promise<typeof task.snapshot>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        try {
          const ratio = snap.totalBytes > 0 ? snap.bytesTransferred / snap.totalBytes : 0;
          onProgress?.(ratio);
        } catch { /* ignore */ }
      },
      reject,
      () => resolve(task.snapshot),
    );
  });

  const downloadURL = await getDownloadURL(snapshot.ref);

  const expiresAt = Timestamp.fromDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));

  return {
    id: attachmentId,
    fileName: sanitized,
    mimeType: file.type || 'application/octet-stream',
    size: file.size,
    storagePath,
    downloadURL,
    attachmentType: 'temporary',
    expiresAt,
  };
};

